import React, { useCallback, useEffect, useRef, useState } from "react";

import * as styles from "./styles";
import { editorAgentUrl } from "utils/visualAgentUrls";
import { buildVisualSrcdoc } from "utils/visualSrcdoc";

/**
 * Renders a Willow Create visual (self-contained HTML) inside a sandboxed iframe and
 * lets the user edit its text directly in place. Edited HTML is reported back via
 * onChange; on save the backend re-renders it to overwrite the PNG/PDF in place.
 *
 * The editing logic lives in editorAgent.js, loaded into the iframe as a same-origin script.
 * The script talks to this component only over postMessage (the iframe has an opaque origin —
 * sandbox with allow-scripts but NOT allow-same-origin — so it cannot reach the parent DOM).
 *
 * Mechanism validated in docs/prototypes/editable-visual.
 */

const SLIDE_WIDTH = 1080;
const SLIDE_HEIGHT = 1350;
const DEFAULT_DISPLAY_WIDTH = 380;

interface EditableVisualProps {
  html: string;
  onChange: (html: string) => void;
  onOverflow?: (clippedSlides: number[]) => void; // slide indexes whose text is cut off, each time the editor measures
  // Upper bound on rendered width in px (the 4:5 canvas is scaled to fit).
  displayWidth?: number;
}

const ASPECT = SLIDE_WIDTH / SLIDE_HEIGHT; // 0.8 (4:5 portrait)
const MAX_VISUAL_HEIGHT = 560;

const EditableVisual: React.FC<EditableVisualProps> = ({
  html,
  onChange,
  onOverflow,
  displayWidth = DEFAULT_DISPLAY_WIDTH
}) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const lastEmitted = useRef<string | null>(null);

  const [srcdoc, setSrcdoc] = useState<string>(() => buildVisualSrcdoc(html, editorAgentUrl));
  const [slides, setSlides] = useState(1);
  const [current, setCurrent] = useState(0);
  const [clipped, setClipped] = useState<number[]>([]);
  const [overflowPx, setOverflowPx] = useState(0);
  const [viewportHeight, setViewportHeight] = useState(
    () => (typeof window !== "undefined" ? window.innerHeight : 900)
  );

  // Fit the visual to the screen: cap height to ~55% of the viewport (and an absolute cap),
  // then derive width from that — so it never dominates a laptop screen.
  const cappedHeight = Math.min(MAX_VISUAL_HEIGHT, Math.round(viewportHeight * 0.55));
  const displayWidthFitted = Math.min(displayWidth, Math.round(cappedHeight * ASPECT));
  const displayHeight = Math.round(displayWidthFitted / ASPECT);
  const scale = displayWidthFitted / SLIDE_WIDTH;

  // Keep the fit-to-viewport sizing responsive to window resizes.
  useEffect(() => {
    const onResize = () => setViewportHeight(window.innerHeight);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Rebuild the iframe only for an externally-changed visual (a different attachment),
  // never for our own edits — otherwise the iframe reloads and the caret is lost.
  useEffect(() => {
    if (html !== lastEmitted.current) {
      setSrcdoc(buildVisualSrcdoc(html, editorAgentUrl));
      setCurrent(0);
      setClipped([]);
      setOverflowPx(0);
    }
  }, [html]);

  // targetOrigin is "*" because a srcDoc iframe has an opaque ("null") origin that cannot be
  // named here. That is safe: the iframe is sandboxed without allow-same-origin so it can't
  // touch the parent, and inbound messages are filtered by event.source + data.source below.
  const toAgent = useCallback((message: Record<string, unknown>) => {
    iframeRef.current?.contentWindow?.postMessage({ target: "editable-visual", ...message }, "*");
  }, []);

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      // Only trust messages from our own iframe; ignore anything else on the bus.
      if (event.source !== iframeRef.current?.contentWindow) return;
      const data = event.data || {};
      if (data.source !== "editable-visual") return;

      if (data.type === "ready") {
        setSlides(data.slides);
      } else if (data.type === "slide") {
        setCurrent(data.index);
      } else if (data.type === "overflow") {
        setClipped(data.clipped || []);
        setOverflowPx(data.overflowPx || 0);
        onOverflow?.(data.clipped || []);
      } else if (data.type === "html") {
        lastEmitted.current = data.html;
        onChange(data.html);
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [onChange, onOverflow]);

  const onIframeLoad = useCallback(() => {
    toAgent({ type: "init" });
    toAgent({ type: "setEdit", value: true });
  }, [toAgent]);

  const isCarousel = slides > 1;
  const isClipping = clipped.length > 0;

  // Reveal shows the clipped text below a dotted line. overflowPx is the overflow of the slide in
  // view, so this works for both single visuals and carousels.
  const reveal = overflowPx > 0;
  const revealSlidePx = reveal ? Math.min(overflowPx, SLIDE_HEIGHT) : 0;
  const revealDisplayPx = Math.round(revealSlidePx * scale);
  const iframeHeight = SLIDE_HEIGHT + revealSlidePx;

  return (
    <div style={{ width: displayWidthFitted, margin: "0 auto" }}>
      <div
        style={{
          width: displayWidthFitted,
          height: displayHeight + revealDisplayPx,
          overflow: "hidden",
          borderRadius: 12,
          border: isClipping ? "2px solid #f43f5e" : "1px solid #e2e8f0",
          background: "#fff",
          position: "relative"
        }}
      >
        <iframe
          ref={iframeRef}
          title="editable-visual"
          sandbox="allow-scripts"
          srcDoc={srcdoc}
          onLoad={onIframeLoad}
          style={{ width: SLIDE_WIDTH, height: iframeHeight, border: 0, transform: `scale(${scale})`, transformOrigin: "top left" }}
        />
        {reveal && (
          <>
            <div
              style={{
                position: "absolute",
                left: 0,
                right: 0,
                top: displayHeight,
                height: revealDisplayPx,
                background: "rgba(15,23,42,.45)",
                pointerEvents: "none"
              }}
            />
            <div
              style={{
                position: "absolute",
                left: 0,
                right: 0,
                top: displayHeight - 1,
                borderTop: "2px dotted #f43f5e",
                pointerEvents: "none"
              }}
            />
          </>
        )}
        {isCarousel && current > 0 && (
          <styles.SlideArrow
            $side="left"
            type="button"
            aria-label="Previous slide"
            // Prevent the button from taking focus: a focused control inside a horizontally
            // overflowing composer makes the browser scroll the panel to reveal it.
            onMouseDown={e => e.preventDefault()}
            onClick={() => toAgent({ type: "go", index: current - 1 })}
          >
            ‹
          </styles.SlideArrow>
        )}
        {isCarousel && current < slides - 1 && (
          <styles.SlideArrow
            $side="right"
            type="button"
            aria-label="Next slide"
            onMouseDown={e => e.preventDefault()}
            onClick={() => toAgent({ type: "go", index: current + 1 })}
          >
            ›
          </styles.SlideArrow>
        )}
      </div>

      {isClipping && (
        <div style={{ marginTop: 8, fontSize: 12.5, lineHeight: 1.45, color: "#be123c" }}>
          {isCarousel
            ? `⚠︎ Some text on slide ${clipped.map(i => i + 1).join(", ")} is pushed below the bottom of the page and will be cut off when the visual is rendered. Remove some text to fix this.`
            : "⚠︎ Some text is pushed below the bottom of the page and may be cut off when the visual is rendered. Remove text to fix this."}
        </div>
      )}
    </div>
  );
};

export default EditableVisual;
